import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';

import { getSavedSizeSelectionId, type SavedSize } from '@air/feature-saved-sizes/utils';

import { SizeValue } from '~/components/DimensionBar/DimensionBar';
import { type ExportImagesFormData } from '~/components/ExportImagesModal/ExportImagesModal';
import { getSavedSizeOptionLabel } from '~/components/ExportImagesModal/utils/savedSizeOption';
import { FormikSingleSelect } from '~/components/Formik/FormikSingleSelect';
import { defaultImageSizeOptions } from '~/components/ImageFormatFormField';
import { IMAGE_SIZE_SINGLE_SELECT } from '~/constants/testIDs';

export type ExportImageSizeSelectProps = {
  setIsEditing: (isFocused: boolean) => void;
  sizePresets: SavedSize[];
};

export const ExportImageSizeSelect = ({ setIsEditing, sizePresets }: ExportImageSizeSelectProps) => {
  const { setFieldValue, values } = useFormikContext<ExportImagesFormData>();
  const options = useMemo(() => {
    const customOption = defaultImageSizeOptions.find(({ value }) => value === SizeValue.Custom);

    return [
      ...defaultImageSizeOptions.filter(({ value }) => value !== SizeValue.Custom),
      ...sizePresets.map((savedSize) => ({
        label: getSavedSizeOptionLabel(savedSize),
        value: getSavedSizeSelectionId(savedSize.id),
      })),
      ...(customOption ? [customOption] : []),
    ];
  }, [sizePresets]);
  const selectedSavedSize = sizePresets.find((savedSize) => getSavedSizeSelectionId(savedSize.id) === values.size);

  useEffect(() => {
    if (
      !selectedSavedSize ||
      (values.width === selectedSavedSize.width && values.height === selectedSavedSize.height)
    ) {
      return;
    }

    void setFieldValue('width', selectedSavedSize.width);
    void setFieldValue('height', selectedSavedSize.height);
  }, [selectedSavedSize, setFieldValue, values.height, values.width]);

  const handleOptionsClose = useCallback(() => setIsEditing(false), [setIsEditing]);
  const handleOptionsOpen = useCallback(() => setIsEditing(true), [setIsEditing]);

  return (
    <FormikSingleSelect
      isSearchable={false}
      size="large"
      className="w-full"
      data-testid={IMAGE_SIZE_SINGLE_SELECT}
      label="Size"
      name="size"
      options={options}
      onOptionsClose={handleOptionsClose}
      onOptionsOpen={handleOptionsOpen}
    />
  );
};
